import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShoppingBag } from "lucide-react";
import type { FavoriteProduct } from "@/api/hooks";
import { useProductDetailModal } from "@/features/product-detail/hooks/use-product-detail-modal";
import { formatPrice } from "@/features/orders/utils/format-price";
import { isModifiedClick } from "@/lib/link-utils";
import { cn } from "@/lib/utils";
import { useCommercePaths } from "@/modules";
import { FavoriteButton } from "./FavoriteButton";

interface FavoriteProductCardProps {
  product: FavoriteProduct;
  /** Satırdan çözülen ürün id'si; yoksa kart linksiz ve kalpsiz çizilir. */
  productId: string | null;
  className?: string;
}

/**
 * Favori ürün kartı — favoriler sayfası ve sepet şeridi aynı kartı kullanır.
 * Sol tık modal, Cmd/orta tık gerçek ürün sayfası (link semantiği).
 */
export const FavoriteProductCard: React.FC<FavoriteProductCardProps> = ({
  product,
  productId,
  className,
}) => {
  const { t } = useTranslation();
  const paths = useCommercePaths();
  const { openModal } = useProductDetailModal();

  const image = product.images?.[0]?.imageUrl || "";
  const offerId = product.offerId || undefined;
  const listPrice = product.prices?.listPrice?.value ?? 0;
  const salePrice = product.prices?.salePrice?.value ?? 0;
  const currency =
    product.prices?.salePrice?.currency ||
    product.prices?.listPrice?.currency ||
    "TRY";
  const effectivePrice = salePrice > 0 ? salePrice : listPrice;
  const discount =
    listPrice > effectivePrice && effectivePrice > 0
      ? Math.round(((listPrice - effectivePrice) / listPrice) * 100)
      : null;
  const storeName = product.sellerName || product.brandName;

  const body = (
    <>
      <div className="relative mb-2 aspect-square overflow-hidden rounded-xl bg-gray-100 dark:bg-gray-800">
        {image ? (
          <img
            src={image}
            alt={product.name}
            loading="lazy"
            decoding="async"
            className="size-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
          />
        ) : (
          <div className="flex size-full items-center justify-center text-gray-400 dark:text-gray-600">
            <ShoppingBag size={28} />
          </div>
        )}

        {discount !== null && (
          <span className="absolute left-2 top-2 rounded-md bg-module-accent px-1.5 py-0.5 text-[11px] font-bold text-module-accent-foreground shadow-sm">
            −{discount}%
          </span>
        )}

        {!product.inStock && (
          <span className="absolute inset-x-0 bottom-0 bg-gray-900/70 py-1 text-center text-[11px] font-medium text-white">
            {t("productDetail.outOfStock")}
          </span>
        )}

        <FavoriteButton
          productId={productId}
          className="absolute right-2 top-2 size-8 bg-white/90 shadow-sm backdrop-blur hover:bg-white dark:bg-gray-900/85 dark:hover:bg-gray-900"
        />
      </div>

      <div className="flex flex-col gap-0.5 px-1 pb-1">
        {storeName && (
          <span className="truncate text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-500">
            {storeName}
          </span>
        )}
        <span className="line-clamp-2 min-h-[2.5rem] text-sm text-gray-900 dark:text-gray-100">
          {product.name}
        </span>
        {effectivePrice > 0 && (
          <span className="flex items-baseline gap-1.5 text-sm font-semibold text-gray-900 dark:text-gray-50">
            {formatPrice(effectivePrice, currency)}
            {discount !== null && (
              <span className="text-xs font-normal text-gray-400 line-through dark:text-gray-500">
                {formatPrice(listPrice, currency)}
              </span>
            )}
          </span>
        )}
      </div>
    </>
  );

  const cardClassName = cn(
    "group block rounded-2xl border border-gray-200 p-1.5 transition-shadow hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-module-accent/60 dark:border-gray-700",
    className,
  );

  // Id çözülemeyen satır (varyantsız eski gövde) tıklanabilir olmamalı:
  // yanlış ürüne götürmektense düz kart çizilir.
  if (!productId) {
    return <div className={cardClassName}>{body}</div>;
  }

  return (
    <Link
      to={paths.product(productId, { offerId })}
      onClick={(event) => {
        if (isModifiedClick(event)) return;
        event.preventDefault();
        openModal(productId, offerId ?? null);
      }}
      className={cardClassName}
    >
      {body}
    </Link>
  );
};
